/**
 * Endpoints du domaine fee-engine (frais & tarification) — source canonique.
 * Chemins copiés TELS QUELS de la collection Postman / du guide §6.5
 * (préfixe gateway `/gw/fee-engine`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 */
export const feeEngineEndpoints = {
  feeQuotes: {
    /**
     * POST — devis de frais synchrone (§6.5). Consommé par toutes les
     * previews d'opération (initiate transaction, scan-to-pay marchand…).
     * Idempotency-Key OBLIGATOIRE (guide §4.7) : même clé + même corps
     * rejoue la réponse d'origine ; corps différent → 422.
     */
    create: "/gw/fee-engine/api/v1/fee-quotes",
  },
  feeRules: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée une règle en DRAFT. */
    add: "/gw/fee-engine/api/v1/FeeRules/add",
    /** POST — liste paginée, corps `ListRequest<FeeRuleFilter>`. */
    all: "/gw/fee-engine/api/v1/FeeRules/all",
    /** GET — détail d'une règle et de ses paliers. */
    detail: (feeRuleId: string) =>
      `/gw/fee-engine/api/v1/FeeRules/${feeRuleId}`,
    /**
     * POST — flashpay-superadmin, flashpay-admin. Active une règle. Action
     * protégée maker-checker (§5.4) : exige `?approvalRequestId=...` d'une
     * demande FEE_RULE_CHANGE APPROVED (exécutée depuis la page détail
     * d'une ApprovalRequest — phase 8).
     */
    activate: (feeRuleId: string) =>
      `/gw/fee-engine/api/v1/FeeRules/${feeRuleId}/activate`,
    /** POST — flashpay-superadmin, flashpay-admin. Archive une règle. */
    archive: (feeRuleId: string) =>
      `/gw/fee-engine/api/v1/FeeRules/${feeRuleId}/archive`,
  },
  promotions: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée une promotion. */
    add: "/gw/fee-engine/api/v1/Promotions/add",
    /** POST — liste paginée, corps `ListRequest<PromotionFilter>`. */
    all: "/gw/fee-engine/api/v1/Promotions/all",
    /** GET — détail d'une promotion et de ses quotas d'usage. */
    detail: (promotionId: string) =>
      `/gw/fee-engine/api/v1/Promotions/${promotionId}`,
    /** POST — flashpay-superadmin, flashpay-admin. Active une promotion. */
    activate: (promotionId: string) =>
      `/gw/fee-engine/api/v1/Promotions/${promotionId}/activate`,
    /** POST — flashpay-superadmin, flashpay-admin. Désactive une promotion. */
    disable: (promotionId: string) =>
      `/gw/fee-engine/api/v1/Promotions/${promotionId}/disable`,
  },
  subscriptionPlans: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée un plan d'abonnement. */
    add: "/gw/fee-engine/api/v1/SubscriptionPlans/add",
    /** POST — liste paginée, corps `ListRequest<SubscriptionPlanFilter>`. */
    all: "/gw/fee-engine/api/v1/SubscriptionPlans/all",
    /** GET — détail d'un plan d'abonnement. */
    detail: (subscriptionPlanId: string) =>
      `/gw/fee-engine/api/v1/SubscriptionPlans/${subscriptionPlanId}`,
  },
  companySubscriptions: {
    /**
     * POST — flashpay-superadmin, flashpay-admin. Souscrit une entreprise
     * à un plan d'abonnement.
     */
    add: "/gw/fee-engine/api/v1/CompanySubscriptions/add",
    /** POST — liste paginée, corps `ListRequest<CompanySubscriptionFilter>`. */
    all: "/gw/fee-engine/api/v1/CompanySubscriptions/all",
    /** GET — détail d'un abonnement (avec prochaine date de facturation). */
    detail: (companySubscriptionId: string) =>
      `/gw/fee-engine/api/v1/CompanySubscriptions/${companySubscriptionId}`,
    /** POST — flashpay-superadmin, flashpay-admin. Annule un abonnement. */
    cancel: (companySubscriptionId: string) =>
      `/gw/fee-engine/api/v1/CompanySubscriptions/${companySubscriptionId}/cancel`,
  },
} as const;
